import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Download, Clock, CheckCircle, Package, LogOut, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API, useAuth } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user is B2B (from localStorage)
  const isB2B = localStorage.getItem("cliptour_isB2B") === "true";
  const homePath = isB2B ? "/partners" : "/";

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { redirectTo: "/dashboard" } });
      return;
    }

    if (user) {
      fetchOrders();
    }
  }, [user, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: "bg-blue-100 text-blue-700",
      processing: "bg-yellow-100 text-yellow-700",
      delivered: "bg-green-100 text-green-700"
    };
    
    return (
      <Badge className={`${styles[status] || "bg-slate-100 text-slate-700"} font-medium`}>
        {status === "confirmed" && <Clock className="w-3 h-3 mr-1" />}
        {status === "delivered" && <CheckCircle className="w-3 h-3 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getServiceName = (type) => {
    return type === "cliptour_staged" ? "Cliptour Staged" : "Cliptour";
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner w-8 h-8 border-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Header homePath={homePath} />

      <section className="section-spacing pt-32">
        <div className="container-custom">
          {/* Header */}
          <motion.div 
            className="flex flex-col md:flex-row md:items-center md:justify-between mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
              <p className="text-slate-600">Welcome back, {user?.name}</p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <Button
                onClick={() => navigate("/order")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                data-testid="new-order-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Order
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="rounded-full px-6"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </motion.div>

          {/* Orders */}
          {orders.length === 0 ? (
            <motion.div 
              className="bg-white rounded-3xl border border-slate-200 p-12 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No orders yet</h2>
              <p className="text-slate-600 mb-6">Create your first order to get started</p>
              <Button
                onClick={() => navigate("/order")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
              >
                Create Order
              </Button>
            </motion.div>
          ) : (
            <motion.div 
              className="bg-white rounded-3xl border border-slate-200 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="orders-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Date</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Service</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Amount</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-6 text-slate-900">{formatDate(order.created_at)}</td>
                        <td className="py-4 px-6">
                          <span className="font-medium text-slate-900">{getServiceName(order.service_type)}</span>
                          {order.discount_applied && (
                            <span className="ml-2 text-xs text-green-600 font-medium">-$100</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-slate-900">${order.amount.toFixed(2)}</td>
                        <td className="py-4 px-6">{getStatusBadge(order.status)}</td>
                        <td className="py-4 px-6">
                          {order.delivery_link ? (
                            <a
                              href={order.delivery_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-#3C59B2 hover:text-#2d4a8f font-medium text-sm"
                              data-testid={`download-link-${order.order_id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-slate-400 text-sm">Processing...</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
